import { CSSProperties, memo, useCallback, useMemo } from 'react';
import { ColumnRowProps } from '../../ColumnRowProps';
import { SubHeader } from './SubHeader';
import { useGridRowContext } from '../../../../../../../_grid/contexts/rowContext';
import { useScrollToContext } from '../../../../context/ScrollToContext';
import { Stack } from '@fluentui/react';
import { ColoredTotalCell } from './ColoredTotalCell';
import { CategoryWithAllocationColoredTotalCell } from './CategoryWithAllocationColoredTotalCell';
import { ResourceType } from '../../../../../../../../../Entities/ResourceType';
import { bigZero } from '../../../../../../../../../helpers/math';
import { getCellsSelector } from '../../../../../../../_grid/useGrid';
import { useRecoilValue } from 'recoil';

export const ResourceColumnRow = memo(function ResourceColumnRow({ columnRow, expanded, columnIndex }: ColumnRowProps & { expanded: boolean }) {
    const { toggleExpanded, rowIndex } = useGridRowContext();
    const hasCategoryAllocation = useCategoryHasAllocation(columnIndex);
    const render = useMemo(() => {
        return columnRow?.cells.map((cell, i) => {
            if (hasCategoryAllocation) {
                return <CategoryWithAllocationColoredTotalCell key={cell.id} cell={cell} isRowExpanded={expanded} />;
            }
            return <ColoredTotalCell key={cell.id} cell={cell} isRowExpanded={expanded} totalColumnRow={columnRow} />;
        });
    }, [columnRow, expanded, hasCategoryAllocation]);

    const renderColumns = useMemo(() => {
        return (
            <ColumnGridContainer>
                <SubHeader subHeader={{ name: 'Unfulfilled' }} />
                <SubHeader subHeader={{ name: 'Allocation' }} />
            </ColumnGridContainer>
        );
    }, []);

    const scrollTo = useScrollToContext();
    const onClick = useCallback(() => {
        if (!expanded) {
            scrollTo({ rowIndex: rowIndex - 1, align: 'start' });
        }
        toggleExpanded();
    }, [expanded, rowIndex, scrollTo, toggleExpanded]);

    return (
        <Stack onClick={onClick} style={containerStyles}>
            <div style={columnContainerStyles}>{render}</div>
            {expanded && renderColumns}
        </Stack>
    );
});

const useCategoryHasAllocation = (columnIndex: number) => {
    const { row, subRows } = useGridRowContext();
    const subColumnsCells = useMemo(() => subRows.map(subRow => subRow.columnRows[columnIndex].cells[1]).filter(Boolean), [columnIndex, subRows]);
    const cellValues = useRecoilValue(getCellsSelector(subColumnsCells));
    return useMemo(() => {
        return row.metadata?.ResourceType === ResourceType.Category && cellValues.some(value => !value.eq(bigZero));
    }, [cellValues, row.metadata.ResourceType]);
};

const containerStyles: CSSProperties = { height: '100%' };
const columnContainerStyles: CSSProperties = {
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    position: 'relative',
    alignItems: 'center',
    flexGrow: 1,
};

const style: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    alignItems: 'center',
    justifyContent: 'end',
    // height: '100%',
    gap: 10,
    paddingLeft: 10,
    paddingRight: 10,
};
const ColumnGridContainer = ({ children }: { children?: React.ReactNode }) => {
    return <div style={style}>{children}</div>;
};
